import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Client invite-accept — reached from the invite link emailed to a new client user.
 * URL: /client-invite?token=<tok>&sub=<sub_account_id>
 *
 * User picks a password → backend activates the member → redirects to sub-account dashboard.
 */
export default function ClientInviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const subAccountId = searchParams.get('sub');
  const [branding, setBranding] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!subAccountId) return;
    axios.get(`${API}/api/sub-accounts/${subAccountId}/branding`)
      .then(r => setBranding(r.data)).catch(() => {});
  }, [subAccountId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error('Invalid invite'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords don\'t match'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/client/accept-invite`, { token, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('voxinity_token', res.data.token);
      localStorage.setItem('auth_token', res.data.token);
      toast.success('Welcome aboard!');
      // Need workspace_id to navigate — fetch sub-account branding already did; use member response
      const sub = res.data.sub_account_id;
      // Get workspace_id via branding call
      const b = await axios.get(`${API}/api/sub-accounts/${sub}/branding`);
      navigate(`/workspace/${b.data.workspace_id}/sub-account/${sub}/dashboard`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite acceptance failed');
    } finally { setSubmitting(false); }
  };

  const accent = branding?.theme_color || '#10b981';

  if (!token || !subAccountId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#08090d] text-gray-300 p-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold mb-2">Invalid invite link</h1>
          <p className="text-sm text-gray-500">Ask your administrator to re-send the invite.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#08090d] px-4" data-testid="client-invite-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={branding.business_name} className="mx-auto mb-4 max-h-16 object-contain" />
          ) : (
            <div
              className="mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}90)` }}
            >
              {(branding?.business_name || 'C').charAt(0)}
            </div>
          )}
          <h1 className="text-2xl font-semibold text-white">{branding?.business_name || 'Welcome'}</h1>
          <p className="text-sm text-gray-400 mt-1">
            Set your password to access <span className="text-white font-medium">{branding?.sub_account_name || 'your account'}</span>
          </p>
        </div>

        <form onSubmit={submit} className="bg-[#0c0d12] border border-white/[0.06] rounded-2xl p-6 space-y-4" data-testid="client-invite-form">
          <div>
            <label className="text-xs text-gray-400 font-medium mb-1.5 block">New password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-teal-400/50"
                placeholder="Min 8 characters"
                data-testid="client-invite-password"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 font-medium mb-1.5 block">Confirm password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                required type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-teal-400/50"
                data-testid="client-invite-confirm"
              />
            </div>
          </div>
          <button
            type="submit" disabled={submitting}
            className="w-full py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2"
            style={{ background: accent }}
            data-testid="client-invite-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {submitting ? 'Activating…' : 'Activate & sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
